import { InfoCircleOutlined } from "@ant-design/icons";
import { useGetGstInvoices } from "@entities/financeApproval/lib/hooks/useGetGstInvoices";
import {
  GstFilledStatusByVendor,
  IGstInvoice,
  IGstInvoicesRequest,
} from "@shared/lib/types/finance";
import {
  Avatar,
  Button,
  DatePicker,
  Divider,
  Drawer,
  Flex,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { useMemo, useState } from "react";

import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import { BulkGstPaymentConfirmation } from "@features/bulkGstPaymentConfirmation";
import { DownloadGstInvoices } from "@features/DownloadGstInvoices";
import UploadGstBilling from "@features/uploadGstBilling/UploadGstBilling";
import { VendorPayment } from "@features/vendorPayment/VendorPayment";
import {
  FinanceBasePayoutFlags,
  FinanceGstPayoutFlags,
} from "@shared/lib/types/briefCampains";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import styles from "./styles.module.scss";

const { Search } = Input;

export interface TotalRow {
  _id: "total";
  baseCost: number;
  gstAmount: number;
}

export function GstInvoices() {
  const [paymentModalProps, setPaymentModalProps] = useState<{
    visible: boolean;
    id: string;
  }>({
    visible: false,
    id: "",
  });

  const [isBulkPamentModalVisible, setIsBulkPamentModalVisible] =
    useState<boolean>(false);

  const [
    isDownloadingGstInvoicesModalVisible,
    setIsDownloadingGstInvoicesModalVisible,
  ] = useState<boolean>(false);

  const [uploadGstBillingModalOpen, setUploadGstBillingModalOpen] =
    useState(false);
  const [searchRequest, setSearchRequest] = useState<IGstInvoicesRequest>({
    invoiceDate: {
      from: dayjs().startOf("month").startOf("day").toISOString(),
      to: dayjs().endOf("month").endOf("day").toISOString(),
    },
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IGstInvoice[]>([]);

  const { invoices, gstAmountAggregate, baseCostAggregate, isFetching } =
    useGetGstInvoices(searchRequest);

  const handleSearch = (value: string) => {
    setSearchRequest((prev) => ({ ...prev, searchQuery: value }));
  };

  const handleDateChange = (date: any) => {
    if (date) {
      const startOfMonth = date.startOf("month").startOf("day").toISOString();
      const endOfMonth = date.endOf("month").endOf("day").toISOString();
      setSearchRequest((prev) => ({
        ...prev,
        invoiceDate: { from: startOfMonth, to: endOfMonth },
      }));
    } else {
      setSearchRequest((prev) => ({
        ...prev,
        date: undefined,
      }));
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) {
      return "default";
    }
    switch (status.toLowerCase()) {
      case "pending":
        return "red";
      case "input received":
      case "input-received":
        return "blue";
      case "paid":
        return "green";
      case "not paid":
        return "red";
      default:
        return "default";
    }
  };

  const columns: ColumnsType<IGstInvoice | TotalRow> = [
    {
      title: "Creator",
      dataIndex: "campaignInfluencer",
      key: "creator",
      width: 200,
      render: (_, record) => {
        if (record._id === "total") {
          return <div>Total</div>;
        }
        const gstInvoiceRecord = record as IGstInvoice;
        return (
          <Space>
            <Avatar
              size={40}
              src={
                gstInvoiceRecord.campaignInfluencer.socialMedia.profilePicUrl
              }
            />
            <Flex vertical>
              <div className={styles.creatorName}>
                <TextWithTooltip
                  text={`${gstInvoiceRecord.campaignInfluencer.influencer.firstName} ${gstInvoiceRecord.campaignInfluencer.influencer.lastName}`}
                  limit={30}
                />
              </div>
              <Space
                className={styles.creatorHandle}
                size={2}
                onClick={() =>
                  window.open(
                    gstInvoiceRecord.campaignInfluencer.socialMediaLink,
                    "_blank"
                  )
                }
              >
                {getPlatformIcon(
                  gstInvoiceRecord.campaignInfluencer.socialMedia.platform
                )}
                <TextWithTooltip
                  text={
                    gstInvoiceRecord.campaignInfluencer.socialMedia.username
                  }
                  limit={30}
                />
              </Space>
            </Flex>
          </Space>
        );
      },
    },
    {
      title: "Campaign",
      key: "campaign",
      width: 150,
      render: (_, record) => {
        if (record._id === "total") {
          return <></>;
        }
        const gstInvoiceRecord = record as IGstInvoice;
        return (
          <TextWithTooltip
            text={gstInvoiceRecord.campaignInfluencer.brief.name}
            limit={30}
            textClassName={styles.campaignName}
          />
        );
      },
    },
    {
      title: "Invoice No.",
      dataIndex: "remarks",
      key: "invoiceNumber",
      width: 100,
      render: (_, record) => {
        if (record._id === "total") {
          return <></>;
        }
        const gstInvoiceRecord = record as IGstInvoice;
        return (
          <Flex vertical gap={2}>
            <TextWithTooltip
              text={gstInvoiceRecord.remarks}
              limit={30}
              textClassName={styles.invoiceNumber}
            />
            <div className={styles.invoiceDate}>
              {dayjs(gstInvoiceRecord.invoiceDate).format("DD/MM/YYYY")}
            </div>
          </Flex>
        );
      },
    },
    {
      title: "Billing Details",
      dataIndex: "vendorBilling",
      key: "billing",
      width: 200,
      render: (_, record) => {
        if (record._id === "total") {
          return <></>;
        }
        const gstInvoiceRecord = record as IGstInvoice;
        return (
          <Flex vertical gap={2}>
            <TextWithTooltip
              text={gstInvoiceRecord.vendorBilling.name}
              limit={30}
              textClassName={styles.billingName}
            />
            <TextWithTooltip
              text={
                gstInvoiceRecord.vendorBilling.emailId ||
                `${gstInvoiceRecord.vendorBilling.countryCode} ${gstInvoiceRecord.vendorBilling.phoneNumber}`
              }
              limit={30}
              textClassName={styles.billingEmail}
            />

            <div className={styles.billingEmail}>
              {gstInvoiceRecord.vendorBilling.gstNumber || "N/A"}
            </div>
          </Flex>
        );
      },
    },
    {
      title: (
        <Space>
          <Tooltip title="Derived from Invoice">
            <InfoCircleOutlined />
          </Tooltip>
          Base amount
        </Space>
      ),
      dataIndex: "baseCost",
      key: "baseAmount",
      width: 100,
      render: (amount) => <div>₹{amount?.toFixed(2)}</div>,
    },

    {
      title: "Base Amt Status",
      dataIndex: "campaignInfluencerFinanceFlags.basePayout.status",
      key: "basePayoutStatus",
      width: 120,
      render: (_, record) => {
        if (record._id === "total") {
          return <></>;
        }
        const gstInvoiceRecord = record as IGstInvoice;
        return (
          <Tooltip
            title={`₹${(
              (gstInvoiceRecord?.campaignInfluencerFinanceFlags?.basePayout
                ?.total || 0) -
              (gstInvoiceRecord?.campaignInfluencerFinanceFlags?.basePayout
                ?.paid || 0)
            ).toFixed(2)} Left to pay`}
          >
            <Tag
              color={getStatusColor(
                gstInvoiceRecord?.campaignInfluencerFinanceFlags?.basePayout
                  ?.status || ""
              )}
              className={styles.statusTag}
            >
              {gstInvoiceRecord?.campaignInfluencerFinanceFlags?.basePayout
                ?.status || "N/A"}
            </Tag>
          </Tooltip>
        );
      },
    },

    {
      title: (
        <Space>
          <Tooltip title="Derived from Invoice">
            <InfoCircleOutlined />
          </Tooltip>
          Gst amount
        </Space>
      ),
      dataIndex: "gstAmount",
      key: "gstAmount",
      width: 100,
      render: (amount) => <div>₹{amount?.toFixed(2)}</div>,
    },
    {
      title: "GST Status",
      dataIndex: "gstStatus",
      key: "gstStatus",
      width: 150,
      render: (_, record) => {
        if (record._id === "total") {
          return <></>;
        }
        const gstInvoiceRecord = record as IGstInvoice;
        return (
          <Tag
            color={getStatusColor(
              gstInvoiceRecord?.gstFilledStatusByVendor || ""
            )}
            className={styles.statusTag}
          >
            {gstInvoiceRecord?.gstFilledStatusByVendor || "N/A"}
          </Tag>
        );
      },
    },
    {
      title: "GST Payment Status",
      dataIndex: "gstPaymentStatus",
      key: "gstPaymentStatus",
      width: 150,
      render: (_, record) => {
        if (record._id === "total") {
          return <></>;
        }
        const gstInvoiceRecord = record as IGstInvoice;
        return (
          <Tooltip
            title={`₹${
              gstInvoiceRecord?.invoiceFinanceFlags?.gstPayout?.left || 0
            } to pay`}
          >
            <Tag
              color={getStatusColor(
                gstInvoiceRecord?.invoiceFinanceFlags?.gstPayout?.status || ""
              )}
              className={styles.statusTag}
            >
              {gstInvoiceRecord?.invoiceFinanceFlags?.gstPayout?.status ||
                "N/A"}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Payment",
      key: "payment",
      width: 100,
      render: (_, record) => {
        if (record._id === "total") {
          return <></>;
        }
        const gstInvoiceRecord = record as IGstInvoice;
        return (
          <Button
            type="primary"
            size="small"
            onClick={() =>
              setPaymentModalProps({
                visible: true,
                id: gstInvoiceRecord.campaignInfluencerId,
              })
            }
          >
            Payment
          </Button>
        );
      },
    },
  ];

  const selectedbaseCostAggregate = useMemo(() => {
    return selectedRows?.length > 0
      ? selectedRows.reduce((acc, curr) => acc + (curr.baseCost || 0), 0)
      : baseCostAggregate;
  }, [selectedRows, baseCostAggregate]);

  const selectedGstAmountAggregate = useMemo(() => {
    return selectedRows?.length > 0
      ? selectedRows.reduce((acc, curr) => acc + (curr.gstAmount || 0), 0)
      : gstAmountAggregate;
  }, [selectedRows, gstAmountAggregate]);

  return (
    <div>
      <h1 className={styles.title}>Gst Tracker</h1>
      <div className={styles.card}>
        <Space>
          <DatePicker
            onChange={handleDateChange}
            picker="month"
            format="MMM YYYY"
            allowClear={false}
            value={
              searchRequest.invoiceDate?.from
                ? dayjs(searchRequest.invoiceDate?.from)
                : undefined
            }
          />
        </Space>
        <Divider style={{ margin: 0 }} />
        <Row justify={"space-between"} className={styles.actions}>
          <Search
            placeholder="Search by GST Number, Invoice No, Billing Name or Campaign Name"
            onSearch={(val) => handleSearch(val)}
            value={searchRequest.searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: 350 }}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => setUploadGstBillingModalOpen(true)}
            >
              Upload Gst Filing
            </Button>
            <Button
              size="small"
              onClick={() => setIsDownloadingGstInvoicesModalVisible(true)}
            >
              Download Gst Invoices
            </Button>
          </Space>
        </Row>

        <Flex style={{ padding: "0px 20px 0px", gap: 6 }} wrap>
          <Tag
            color={
              searchRequest.gstFilledStatusByVendor?.includes(
                GstFilledStatusByVendor.Pending
              )
                ? "geekblue"
                : "default"
            }
            style={{ cursor: "pointer" }}
            onClick={() =>
              setSearchRequest((prev) => ({
                ...prev,
                gstFilledStatusByVendor: prev.gstFilledStatusByVendor?.includes(
                  GstFilledStatusByVendor.Pending
                )
                  ? prev.gstFilledStatusByVendor?.filter(
                      (status) => status !== GstFilledStatusByVendor.Pending
                    )
                  : [
                      ...(prev.gstFilledStatusByVendor || []),
                      GstFilledStatusByVendor.Pending,
                    ],
              }))
            }
          >
            GST Pending
          </Tag>
          <Tag
            color={
              searchRequest.gstFilledStatusByVendor?.includes(
                GstFilledStatusByVendor["Input-Received"]
              )
                ? "geekblue"
                : "default"
            }
            style={{ cursor: "pointer" }}
            onClick={() =>
              setSearchRequest((prev) => ({
                ...prev,
                gstFilledStatusByVendor: prev.gstFilledStatusByVendor?.includes(
                  GstFilledStatusByVendor["Input-Received"]
                )
                  ? prev.gstFilledStatusByVendor?.filter(
                      (status) =>
                        status !== GstFilledStatusByVendor["Input-Received"]
                    )
                  : [
                      ...(prev.gstFilledStatusByVendor || []),
                      GstFilledStatusByVendor["Input-Received"],
                    ],
              }))
            }
          >
            GST Input Received
          </Tag>
          <Divider type="vertical" style={{ height: "22px", width: "10px" }} />
          <Tag
            color={
              searchRequest.campaignInfluencerFinanceFlags?.basePayout?.statuses?.includes(
                FinanceBasePayoutFlags.Pending
              )
                ? "geekblue"
                : "default"
            }
            style={{ cursor: "pointer" }}
            onClick={() =>
              setSearchRequest((prev) => ({
                ...prev,
                campaignInfluencerFinanceFlags: {
                  ...prev.campaignInfluencerFinanceFlags,
                  basePayout: {
                    statuses:
                      !prev.campaignInfluencerFinanceFlags?.basePayout?.statuses?.includes(
                        FinanceBasePayoutFlags.Pending
                      )
                        ? [
                            ...(prev.campaignInfluencerFinanceFlags?.basePayout
                              ?.statuses || []),
                            FinanceBasePayoutFlags.Pending,
                          ]
                        : prev.campaignInfluencerFinanceFlags?.basePayout?.statuses?.filter(
                            (status) =>
                              status !== FinanceBasePayoutFlags.Pending
                          ),
                  },
                },
              }))
            }
          >
            Base Amt Pending
          </Tag>
          <Tag
            color={
              searchRequest.campaignInfluencerFinanceFlags?.basePayout?.statuses?.includes(
                FinanceBasePayoutFlags.PartiallyPaid
              )
                ? "geekblue"
                : "default"
            }
            style={{ cursor: "pointer" }}
            onClick={() =>
              setSearchRequest((prev) => ({
                ...prev,
                campaignInfluencerFinanceFlags: {
                  ...prev.campaignInfluencerFinanceFlags,
                  basePayout: {
                    statuses:
                      !prev.campaignInfluencerFinanceFlags?.basePayout?.statuses?.includes(
                        FinanceBasePayoutFlags.PartiallyPaid
                      )
                        ? [
                            ...(prev.campaignInfluencerFinanceFlags?.basePayout
                              ?.statuses || []),
                            FinanceBasePayoutFlags.PartiallyPaid,
                          ]
                        : prev.campaignInfluencerFinanceFlags?.basePayout?.statuses?.filter(
                            (status) =>
                              status !== FinanceBasePayoutFlags.PartiallyPaid
                          ),
                  },
                },
              }))
            }
          >
            Base Amt Partial
          </Tag>
          <Tag
            color={
              searchRequest.campaignInfluencerFinanceFlags?.basePayout?.statuses?.includes(
                FinanceBasePayoutFlags.Paid
              )
                ? "geekblue"
                : "default"
            }
            style={{ cursor: "pointer" }}
            onClick={() =>
              setSearchRequest((prev) => ({
                ...prev,
                campaignInfluencerFinanceFlags: {
                  ...prev.campaignInfluencerFinanceFlags,
                  basePayout: {
                    statuses:
                      !prev.campaignInfluencerFinanceFlags?.basePayout?.statuses?.includes(
                        FinanceBasePayoutFlags.Paid
                      )
                        ? [
                            ...(prev.campaignInfluencerFinanceFlags?.basePayout
                              ?.statuses || []),
                            FinanceBasePayoutFlags.Paid,
                          ]
                        : prev.campaignInfluencerFinanceFlags?.basePayout?.statuses?.filter(
                            (status) => status !== FinanceBasePayoutFlags.Paid
                          ),
                  },
                },
              }))
            }
          >
            Base Amt Completed
          </Tag>
          <Divider type="vertical" style={{ height: "22px", width: "10px" }} />
          <Tag
            color={
              searchRequest.invoiceFinanceFlags?.gstPayout?.statuses?.includes(
                FinanceGstPayoutFlags.Pending
              )
                ? "geekblue"
                : "default"
            }
            style={{ cursor: "pointer" }}
            onClick={() =>
              setSearchRequest((prev) => ({
                ...prev,
                invoiceFinanceFlags: {
                  ...prev.invoiceFinanceFlags,
                  gstPayout: {
                    statuses:
                      !prev.invoiceFinanceFlags?.gstPayout?.statuses?.includes(
                        FinanceGstPayoutFlags.Pending
                      )
                        ? [
                            ...(prev.invoiceFinanceFlags?.gstPayout?.statuses ||
                              []),
                            FinanceGstPayoutFlags.Pending,
                          ]
                        : prev.invoiceFinanceFlags?.gstPayout?.statuses?.filter(
                            (status) => status !== FinanceGstPayoutFlags.Pending
                          ),
                  },
                },
              }))
            }
          >
            GST Amt Pending
          </Tag>
          <Tag
            color={
              searchRequest.invoiceFinanceFlags?.gstPayout?.statuses?.includes(
                FinanceGstPayoutFlags.PartiallyPaid
              )
                ? "geekblue"
                : "default"
            }
            style={{ cursor: "pointer" }}
            onClick={() =>
              setSearchRequest((prev) => ({
                ...prev,
                invoiceFinanceFlags: {
                  ...prev.invoiceFinanceFlags,
                  gstPayout: {
                    statuses:
                      !prev.invoiceFinanceFlags?.gstPayout?.statuses?.includes(
                        FinanceGstPayoutFlags.PartiallyPaid
                      )
                        ? [
                            ...(prev.invoiceFinanceFlags?.gstPayout?.statuses ||
                              []),
                            FinanceGstPayoutFlags.PartiallyPaid,
                          ]
                        : prev.invoiceFinanceFlags?.gstPayout?.statuses?.filter(
                            (status) =>
                              status !== FinanceGstPayoutFlags.PartiallyPaid
                          ),
                  },
                },
              }))
            }
          >
            GST Amt Partial
          </Tag>
          <Tag
            color={
              searchRequest.invoiceFinanceFlags?.gstPayout?.statuses?.includes(
                FinanceGstPayoutFlags.Paid
              )
                ? "geekblue"
                : "default"
            }
            style={{ cursor: "pointer" }}
            onClick={() =>
              setSearchRequest((prev) => ({
                ...prev,
                invoiceFinanceFlags: {
                  ...prev.invoiceFinanceFlags,
                  gstPayout: {
                    statuses:
                      !prev.invoiceFinanceFlags?.gstPayout?.statuses?.includes(
                        FinanceGstPayoutFlags.Paid
                      )
                        ? [
                            ...(prev.invoiceFinanceFlags?.gstPayout?.statuses ||
                              []),
                            FinanceGstPayoutFlags.Paid,
                          ]
                        : prev.invoiceFinanceFlags?.gstPayout?.statuses?.filter(
                            (status) => status !== FinanceGstPayoutFlags.Paid
                          ),
                  },
                },
              }))
            }
          >
            GST Amt Completed
          </Tag>
        </Flex>

        {/* <Flex style={{ padding: " 10px 10px", gap: 6 }} wrap></Flex> */}
        <div className={styles.tableContainer}>
          <style>{`.ant-table-row-selected >.ant-table-cell { background: var(--hover-bg) 
      !important; }`}</style>
          <Table
            dataSource={
              [
                {
                  _id: "total",
                  baseCost: selectedbaseCostAggregate,
                  gstAmount: selectedGstAmountAggregate,
                },
                ...invoices,
              ] as (IGstInvoice | TotalRow)[]
            }
            columns={columns}
            loading={isFetching}
            rowKey="_id"
            size="small"
            rowSelection={{
              selectedRowKeys,
              onChange: (selectedRowKeys, selectedRows) => {
                const withoutTotal = selectedRows.filter(
                  (row) => row._id !== "total"
                );
                setSelectedRowKeys(withoutTotal.map((row) => row._id));
                setSelectedRows(withoutTotal as IGstInvoice[]);
              },
              getCheckboxProps: (record: IGstInvoice | TotalRow) => ({
                // Exclude total row from selection
                disabled: record._id === "total",
              }),
            }}
            scroll={{ x: 1000, y: 540 }}
            style={{ marginTop: "20px", overflowX: "auto" }}
            pagination={false}
            rowClassName={(record) =>
              record._id === "total" ? styles.totalRow : ""
            }
          />
          {selectedRowKeys.length > 0 && (
            <div className={styles.selectedRowActionsContainer} style={{}}>
              <Button onClick={() => setIsBulkPamentModalVisible(true)}>
                Bulk Payment ({selectedRowKeys.length})
              </Button>
            </div>
          )}
        </div>
        <Drawer
          title="Payment"
          destroyOnClose
          footer={null}
          width={1200}
          open={paymentModalProps.visible}
          onClose={() =>
            setPaymentModalProps({
              id: "",
              visible: false,
            })
          }
        >
          {paymentModalProps.visible && (
            <VendorPayment
              campaignInfluencerId={paymentModalProps.id || ""}
              onClose={() =>
                setPaymentModalProps({
                  id: "",
                  visible: false,
                })
              }
            />
          )}
        </Drawer>

        <Modal
          title="Upload Gst Billing"
          destroyOnClose
          footer={null}
          width={1200}
          open={uploadGstBillingModalOpen}
          // onClose={() => setUploadGstBillingModalOpen(false)}
          onCancel={() => setUploadGstBillingModalOpen(false)}
        >
          <UploadGstBilling
            onClose={() => setUploadGstBillingModalOpen(false)}
          />
        </Modal>
        <Modal
          title="Bulk Payment"
          destroyOnClose
          footer={null}
          width={500}
          open={isBulkPamentModalVisible}
          // onClose={() => {
          //   setIsBulkPamentModalVisible(false);
          //   setSelectedRowKeys([]);
          //   setSelectedRows([]);
          // }}
          onCancel={() => {
            setIsBulkPamentModalVisible(false);
            setSelectedRowKeys([]);
            setSelectedRows([]);
          }}
        >
          {isBulkPamentModalVisible && (
            <BulkGstPaymentConfirmation
              onClose={() => {
                setIsBulkPamentModalVisible(false);
                setSelectedRowKeys([]);
                setSelectedRows([]);
              }}
              influencers={selectedRows}
              selectedbaseCostAggregate={selectedbaseCostAggregate}
              selectedGstAmountAggregate={selectedGstAmountAggregate}
            />
          )}
        </Modal>
        <Modal
          title="Download Gst Invoices"
          destroyOnClose
          footer={null}
          width={500}
          open={isDownloadingGstInvoicesModalVisible}
          // onClose={() => setIsDownloadingGstInvoicesModalVisible(false)}
          onCancel={() => setIsDownloadingGstInvoicesModalVisible(false)}
        >
          {isDownloadingGstInvoicesModalVisible && (
            <DownloadGstInvoices
              onClose={() => setIsDownloadingGstInvoicesModalVisible(false)}
            />
          )}
        </Modal>
      </div>
    </div>
  );
}
